/**
 * EVENTSFLO-ADMIN-BACKUP-UI-COMPONENT-SPLIT-V1
 *
 * Two-step Create-backup modal. Self-contained: owns the step state,
 * the include_* options object (with `include_uploads` defaulting to
 * `false` exactly like V1), the preflight payload, the typed-EXPORT
 * confirmation and the export-in-flight flag. Calls back into the
 * orchestrator via `onCreated` once the actual export succeeds so the
 * parent can refresh the list / status banner.
 */
import React, { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { toast } from "sonner";
import { preflightExport, runExport } from "./backupApi";

const DEFAULT_OPTS = {
  include_uploads: false,
  include_mongodb_data: true,
  include_schema: true,
  include_gdpr_map: true,
};

export default function BackupCreateDialog({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState(1);
  const [opts, setOpts] = useState({ ...DEFAULT_OPTS });
  const [preflightData, setPreflightData] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [typedConfirm, setTypedConfirm] = useState("");
  const [exporting, setExporting] = useState(false);

  // Reset internal state every time the dialog reopens (V1 behaviour).
  useEffect(() => {
    if (open) {
      setStep(1);
      setOpts({ ...DEFAULT_OPTS });
      setPreflightData(null);
      setTypedConfirm("");
    }
  }, [open]);

  const runPreflight = async () => {
    setPreflightLoading(true);
    try {
      const { data } = await preflightExport(opts);
      setPreflightData(data);
      setStep(2);
    } catch (e) {
      toast.error("Preflight failed");
      console.error(e);
    } finally {
      setPreflightLoading(false);
    }
  };

  const runExportConfirm = async () => {
    if (typedConfirm !== "EXPORT") {
      toast.error("Please type EXPORT to confirm");
      return;
    }
    setExporting(true);
    try {
      const { data } = await runExport(opts);
      toast.success(`Backup created: ${data.filename}`);
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error("Export failed");
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="create-backup-dialog">
        <DialogHeader>
          <DialogTitle>
            Create backup ({step === 1 ? "Step 1: Preflight" : "Step 2: Confirm"})
          </DialogTitle>
          <DialogDescription>
            Generates a server-side ZIP under the pod-ephemeral backup directory. Secrets are
            redacted automatically. Live restore is not available in this build.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3" data-testid="create-step-1">
            <div className="space-y-2">
              {["include_mongodb_data", "include_schema", "include_gdpr_map", "include_uploads"].map((k) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox
                    checked={opts[k]}
                    onCheckedChange={(v) => setOpts((s) => ({ ...s, [k]: !!v }))}
                    data-testid={`opt-${k}`}
                  />
                  <span>{k}</span>
                  {k === "include_uploads" && (
                    <span className="text-xs text-amber-400">(off by default; can be large)</span>
                  )}
                </label>
              ))}
            </div>
            <Button
              onClick={runPreflight}
              disabled={preflightLoading}
              data-testid="run-preflight-btn"
            >
              {preflightLoading ? "Running…" : "Run preflight"}
            </Button>
          </div>
        )}

        {step === 2 && preflightData && (
          <div className="space-y-3" data-testid="create-step-2">
            <div className="text-xs text-zinc-300">
              <div>
                <strong>Collections:</strong> {preflightData.database?.collection_count ?? "—"}
              </div>
              <div>
                <strong>Approx documents:</strong>{" "}
                {(preflightData.database?.approximate_total_documents ?? 0).toLocaleString()}
              </div>
              <div>
                <strong>Sections:</strong> {(preflightData.archive_sections || []).join(", ")}
              </div>
              {preflightData.warning ? (
                <div className="text-amber-400 mt-2">{preflightData.warning}</div>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor="typedConfirm">Type EXPORT to confirm</Label>
              <Input
                id="typedConfirm"
                data-testid="typed-confirm-input"
                value={typedConfirm}
                onChange={(e) => setTypedConfirm(e.target.value)}
                placeholder="EXPORT"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {step === 2 && (
            <Button
              onClick={runExportConfirm}
              disabled={exporting || typedConfirm !== "EXPORT"}
              data-testid="confirm-export-btn"
            >
              {exporting ? "Creating…" : "Create backup"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
